import {
  AccessibleUserAttributes,
  fetchUserGroupInfo,
  getTeamConfigFromLambda,
  getUserAttributes,
} from '@/utils/helpers';
import { create } from 'zustand';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { resetSession } from '@/utils/helpers';
import logger from '@/utils/logger';
import allRoutes, { ProtectedRoutePaths } from '@/lib/routes';
import type { TeamConfigResponse } from '@/types/data';

export type UserAttributtes = Record<AccessibleUserAttributes, string>;

type UseUser = {
  username: string;
  teamName: string;
  isGroupAdmin: boolean;
  isGlobalAdmin: boolean;
  loading: boolean;
  userIsSet: boolean;
  accessibleProtectedRoutes: ProtectedRoutePaths[];
  teamConfig: TeamConfigResponse | null;

  signOut: () => Promise<void>;
  _init: () => void;
};

/**
 * useUserContext is a Zustand store that manages user-related state and actions.
 * It initializes user information, handles sign-out, and provides access to user details.
 * @returns {UseUser} The user context store with user details and actions.
 */
export const useUserContext = create<UseUser>((set, get) => {
  //stores an array of all protected routes the user has access to
  const _buildAccessableRoutes = (userAttributes: UserAttributtes, isAdmin: boolean) => {
    let accessibleProtectedRoutes = Object.values(allRoutes)
      .filter((routeConfig) => routeConfig.protected)
      .map((routeConfig) => routeConfig.route);

    if (isAdmin) {
      return accessibleProtectedRoutes;
    }

    if (userAttributes['custom:scout_access'] !== 'true') {
      //remove the cahtbot route if the user has no scout access
      accessibleProtectedRoutes = accessibleProtectedRoutes.filter((route) => route !== allRoutes.chatbot.route);
    }

    if (userAttributes['custom:club_access'] !== 'true') {
      // remove the qbusiness route if the user has no club access
      accessibleProtectedRoutes = accessibleProtectedRoutes.filter((route) => route !== allRoutes.qbusiness.route);
    }

    if (!isAdmin) {
      // remove the group manager route if the user is not a group admin
      accessibleProtectedRoutes = accessibleProtectedRoutes.filter((route) => route !== allRoutes.groupManager.route);
    }

    return accessibleProtectedRoutes;
  };

  const _initUser = async () => {
    if (get().loading) {
      return;
    }

    set((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const currentUserDetails = await getCurrentUser();
      const { teamName, isGlobalAdmin, isGroupAdmin } = await fetchUserGroupInfo();
      const userAttributes = await getUserAttributes();
      const username = currentUserDetails?.signInDetails?.loginId;
      const accessibleProtectedRoutes = _buildAccessableRoutes(userAttributes, isGlobalAdmin || isGroupAdmin);
      const teamConfig = await getTeamConfigFromLambda(teamName);

      set((state) => ({
        ...state,
        username: username || '',
        teamName: teamName || '',
        isGroupAdmin: isGroupAdmin,
        isGlobalAdmin: isGlobalAdmin,
        accessibleProtectedRoutes,
        teamConfig,
      }));
    } catch (error) {
      logger.error('Error initializing user:', error);
    } finally {
      set((state) => ({
        ...state,
        userIsSet: true,
        loading: false,
      }));
    }
  };

  const _performSignOut = async () => {
    resetSession();
    await signOut({ global: true });
    localStorage.clear();
    sessionStorage.clear();
  };

  return {
    username: '',
    teamName: '',
    isGroupAdmin: false,
    isGlobalAdmin: false,
    loading: false,
    userIsSet: false,
    accessibleProtectedRoutes: [],
    teamConfig: null,

    _init: async () => {
      await _initUser();
    },

    signOut: async () => {
      await _performSignOut();
      set((state) => ({
        ...state,
        username: '',
        teamName: '',
        isGroupAdmin: false,
        isGlobalAdmin: false,
        loading: false,
        userIsSet: false,
        accessibleProtectedRoutes: [],
      }));
    },
  };
});
